import { normalizeAngle } from './geometry.js';

export const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
export const roundPt = (n) => Math.round(n * 100) / 100;

/** Coordinates for annotations are visual PDF points (top-left, at 100% zoom). */
export function visualBounds(descriptor) {
  const angle = normalizeAngle((descriptor.baseRotation || 0) + descriptor.rotation);
  return angle === 90 || angle === 270
    ? { width: descriptor.height, height: descriptor.width }
    : { width: descriptor.width, height: descriptor.height };
}

export function positionWithinPage(rect, bounds) {
  const width = Math.max(1, Math.min(rect.width, bounds.width));
  const height = Math.max(1, Math.min(rect.height, bounds.height));
  return {
    ...rect,
    x: roundPt(clamp(rect.x, 0, Math.max(0, bounds.width - width))),
    y: roundPt(clamp(rect.y, 0, Math.max(0, bounds.height - height))),
    width: roundPt(width), height: roundPt(height),
  };
}

/** Each rectangle has independent edit handles; image sizing preserves its aspect ratio. */
export function resizeRect(rect, handle, dx, dy, bounds, lockAspect = false) {
  const minWidth = Math.min(24, bounds.width);
  const minHeight = Math.min(18, bounds.height);
  const fromLeft = handle.includes('w');
  const fromTop = handle.includes('n');
  const fixedX = fromLeft ? rect.x + rect.width : rect.x;
  const fixedY = fromTop ? rect.y + rect.height : rect.y;
  const signX = fromLeft ? -1 : 1;
  const signY = fromTop ? -1 : 1;
  let width = rect.width + signX * dx;
  let height = rect.height + signY * dy;
  if (lockAspect) {
    const ratio = rect.width / Math.max(rect.height, 0.0001);
    if (Math.abs(signX * dx / Math.max(rect.width, 1)) >= Math.abs(signY * dy / Math.max(rect.height, 1))) {
      height = width / ratio;
    } else {
      width = height * ratio;
    }
    // Bound the size before positioning so images never spill outside a page.
    const maxW = fromLeft ? fixedX : bounds.width - fixedX;
    const maxH = fromTop ? fixedY : bounds.height - fixedY;
    const factor = Math.min(1, maxW / Math.max(width, 0.001), maxH / Math.max(height, 0.001));
    width *= factor;
    height *= factor;
    width = Math.max(Math.min(minWidth, maxW), width);
    height = width / ratio;
    if (height > maxH) { height = maxH; width = height * ratio; }
  } else {
    width = clamp(width, Math.min(minWidth, fromLeft ? fixedX : bounds.width - fixedX), Math.max(0, fromLeft ? fixedX : bounds.width - fixedX));
    height = clamp(height, Math.min(minHeight, fromTop ? fixedY : bounds.height - fixedY), Math.max(0, fromTop ? fixedY : bounds.height - fixedY));
  }
  return {
    ...rect,
    x: roundPt(fromLeft ? fixedX - width : fixedX),
    y: roundPt(fromTop ? fixedY - height : fixedY),
    width: roundPt(width), height: roundPt(height),
  };
}

/** In page display coordinates rotate the center, while keeping added content upright. */
export function rotateAnnotation(rect, oldBounds, angle) {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const clockwise = normalizeAngle(angle) === 90;
  const nx = clockwise ? oldBounds.height - cy : cy;
  const ny = clockwise ? cx : oldBounds.width - cx;
  return positionWithinPage({ ...rect, x: nx - rect.width / 2, y: ny - rect.height / 2 },
    { width: oldBounds.height, height: oldBounds.width });
}

/** Map displayed top-left coordinates (visual points, origin upper left)
 * to unrotated PDF page coordinates, honoring its CropBox and /Rotate. */
export function visualPointToPdf(x, y, cropBox, rotation) {
  const { x: ox, y: oy, width: w, height: h } = cropBox;
  switch (normalizeAngle(rotation)) {
    case 90: return { x: ox + y, y: oy + x };
    case 180: return { x: ox + w - x, y: oy + y };
    case 270: return { x: ox + w - y, y: oy + h - x };
    default: return { x: ox + x, y: oy + h - y };
  }
}
